import { useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useAppDispatch } from "../../../store/hooks";
import { setSnackAction } from "../../../store/slices/snackSlice";
import lensService, { type Playbook } from "../../../services/lensService";
import { apiErrorMessage } from "../lensUtils";

const POLL_EVERY_MS = 2000;
const POLL_FOR_MS = 2 * 60 * 1000;

const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

// Starts a generation without waiting on it: the POST runs to the end, but from the moment the playbook records the
// attempt, its own generation status shows the progress. Only a refusal made before that is reported here.
const useStartGeneration = () => {
    const dispatch = useAppDispatch();
    const queryClient = useQueryClient();
    const [starting, setStarting] = useState(false);
    const mounted = useRef(true);

    useEffect(() => {
        mounted.current = true;
        return () => {
            mounted.current = false;
        };
    }, []);

    const start = async ({
        key,
        request,
        started,
        failMessage,
        onStarted,
    }: {
        key: string;
        request: () => Promise<unknown>;
        // True once the playbook shows this attempt.
        started: (playbook: Playbook) => boolean;
        failMessage: string;
        onStarted?: (playbook: Playbook) => void;
    }) => {
        setStarting(true);
        const post: { settled: boolean; failure: unknown } = { settled: false, failure: null };

        const posted = request()
            .catch((error) => {
                post.failure = error ?? new Error(failMessage);
            })
            .finally(() => {
                post.settled = true;
                queryClient.invalidateQueries({ queryKey: ["lens"] });
            });

        const fail = () => {
            setStarting(false);
            dispatch(setSnackAction(apiErrorMessage(post.failure, failMessage), "error"));
        };

        const giveUpAt = Date.now() + POLL_FOR_MS;
        while (Date.now() < giveUpAt) {
            await Promise.race([wait(POLL_EVERY_MS), posted]);
            const playbook = await lensService.fetchPlaybook(key, true).catch(() => null);
            if (!mounted.current) return;

            // A failure the playbook recorded is shown on its page; anything else was refused before it started.
            if (post.settled && post.failure && playbook?.generationStatus !== "failed") return fail();
            if (playbook && (post.settled || started(playbook))) {
                setStarting(false);
                onStarted?.(playbook);
                return;
            }
            if (post.settled) return fail();
        }

        if (!mounted.current) return;
        setStarting(false);
        dispatch(setSnackAction("We couldn't confirm the generation started — refresh the page to check.", "warning"));
    };

    return { starting, start };
};

export default useStartGeneration;
